import { useMemo, useState } from 'react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { Card, CardHeader, ErrorState, FilterBar, GridKartu, PageHeader, SkeletonGrafik, SkeletonKartu, StatCard, FILTER_KOSONG } from '@/components'
import type { NilaiFilter } from '@/components'
import { awalTahunIni, formatAngka, formatRupiah, tanggalHariIni } from '@/lib/format'
import { cariAngka, cariNilai } from '@/components/LaporanTampil'
import { useApi } from '@/lib/useApi'
import { useOpsiUnit } from '@/lib/opsi'

type BarisGrafik = Record<string, string | number>

const WARNA_PIE = ['#166534', '#b45309', '#1d4ed8', '#7e22ce', '#0e7490', '#be123c', '#4d7c0f', '#a16207']

const KANDIDAT_LABEL = ['label', 'bulan', 'periode', 'name', 'nama', 'tanggal', 'unit', 'kategori', 'kode', 'akun']

/** Menormalkan array dari API menjadi baris grafik (angka & teks saja). */
function keBarisGrafik(nilai: unknown): BarisGrafik[] {
  if (!Array.isArray(nilai)) return []
  const hasil: BarisGrafik[] = []
  for (const item of nilai) {
    if (item === null || typeof item !== 'object') continue
    const baris: BarisGrafik = {}
    for (const [k, v] of Object.entries(item as Record<string, unknown>)) {
      if (typeof v === 'number') baris[k] = v
      else if (typeof v === 'string') {
        const n = Number(v)
        baris[k] = v.trim() !== '' && !Number.isNaN(n) ? n : v
      }
    }
    if (Object.keys(baris).length > 0) hasil.push(baris)
  }
  return hasil
}

function kunciPertama(baris: BarisGrafik[], kandidat: string[]): string | null {
  if (baris.length === 0) return null
  const tersedia = new Set(Object.keys(baris[0]))
  for (const k of kandidat) if (tersedia.has(k)) return k
  return null
}

function kunciAngka(baris: BarisGrafik[], kandidat: string[]): string | null {
  return kunciPertama(baris, kandidat)
}

export default function Dashboard() {
  const opsiUnit = useOpsiUnit()
  const [filter, setFilter] = useState<NilaiFilter>({
    ...FILTER_KOSONG,
    dari: awalTahunIni(),
    sampai: tanggalHariIni(),
  })

  const unitTerpilih = filter.unitId
  const path = unitTerpilih ? `/dashboard/unit/${unitTerpilih}` : '/dashboard/yayasan'

  const { data, memuat, galat, kodeGalat, muat } = useApi<unknown>(path, {
    dari: filter.dari || undefined,
    sampai: filter.sampai || undefined,
  })

  const kpi = useMemo(
    () => [
      {
        label: 'Total Penerimaan',
        nilai: cariAngka(data, ['totalPenerimaan', 'penerimaan', 'totalPendapatan', 'pendapatan', 'totalKasMasuk']),
        nada: 'hijau' as const,
      },
      {
        label: 'Total Pengeluaran',
        nilai: cariAngka(data, ['totalPengeluaran', 'pengeluaran', 'totalBeban', 'beban', 'totalKasKeluar']),
        nada: 'merah' as const,
      },
      {
        label: 'Surplus / Defisit',
        nilai: cariAngka(data, ['surplusDefisit', 'surplus', 'defisit', 'selisih', 'labaRugi']),
        nada: 'netral' as const,
      },
      {
        label: 'Saldo Kas & Bank',
        nilai: cariAngka(data, ['saldoKasBank', 'totalSaldoKas', 'saldoAkhir', 'totalSaldo']),
        nada: 'emas' as const,
      },
      { label: 'Total Piutang (Tunggakan)', nilai: cariAngka(data, ['totalPiutang', 'totalTunggakan', 'piutang', 'outstanding']), nada: 'merah' as const },
      { label: 'Jumlah Santri Aktif', nilai: cariAngka(data, ['jumlahSantri', 'totalSantri', 'santriAktif']), nada: 'netral' as const },
      { label: 'Tagihan Belum Lunas', nilai: cariAngka(data, ['jumlahTagihanBelumLunas', 'tagihanBelumLunas', 'invoicesUnpaid']), nada: 'emas' as const },
      { label: 'Anggaran Terserap', nilai: cariAngka(data, ['persentaseSerapan', 'serapanAnggaran', 'pctSerapan']), nada: 'netral' as const },
    ],
    [data],
  )

  const seriBulanan = useMemo(
    () => keBarisGrafik(cariNilai(data, ['penerimaanPengeluaran', 'penerimaanVsPengeluaran', 'arusBulanan', 'bulanan', 'trenBulanan', 'ringkasanBulanan', 'perBulan'])),
    [data],
  )
  const seriSaldo = useMemo(
    () => keBarisGrafik(cariNilai(data, ['trenSaldoKasBank', 'trenSaldo', 'saldoKasBank', 'saldoKas', 'saldo'])),
    [data],
  )
  const seriAnggaran = useMemo(
    () => keBarisGrafik(cariNilai(data, ['anggaranRealisasi', 'anggaranVsRealisasi', 'realisasiAnggaran', 'perUnit', 'ringkasanUnit'])),
    [data],
  )
  const seriKategori = useMemo(
    () => keBarisGrafik(cariNilai(data, ['pengeluaranPerKategori', 'kategoriPengeluaran', 'pengeluaranKategori', 'kategori'])),
    [data],
  )

  const adaKpi = kpi.some((k) => typeof k.nilai === 'number')

  return (
    <div>
      <PageHeader
        judul="Dashboard"
        deskripsi="Ringkasan posisi keuangan yayasan: penerimaan, pengeluaran, saldo kas & bank, serta serapan anggaran."
      />

      <FilterBar
        nilai={filter}
        onUbah={setFilter}
        unit={opsiUnit}
        tampilkan={{ unitId: true, dari: true, sampai: true, fundId: false, program: false, kategori: false }}
      />

      {galat ? <ErrorState pesan={galat} kode={kodeGalat} onCoba={muat} /> : null}

      {!galat && memuat && !adaKpi ? (
        <div className="space-y-5">
          <SkeletonKartu jumlah={8} />
          <SkeletonGrafik />
        </div>
      ) : null}

      {!galat && adaKpi ? (
        <>
          <GridKartu>
            {kpi.map((k) => (
              <StatCard
                key={k.label}
                label={k.label}
                nilai={k.nilai}
                nada={k.nada}
                formatNilai={(v) =>
                  v === null || v === undefined
                    ? '—'
                    : k.label.includes('Jumlah') || k.label.includes('Tagihan')
                      ? formatAngka(Number(v))
                      : formatRupiah(Number(v))
                }
              />
            ))}
          </GridKartu>
        </>
      ) : null}

      {!galat && !memuat && !adaKpi ? (
        <Card>
          <p className="py-4 text-center text-sm text-netral-500">
            Endpoint dashboard belum mengembalikan indikator untuk periode ini.
          </p>
        </Card>
      ) : null}

      <div className="mt-5 grid grid-cols-1 gap-5 xl:grid-cols-2">
        <GrafikBatang
          judul="Penerimaan vs Pengeluaran per Bulan"
          deskripsi="Perbandingan arus masuk dan arus keluar setiap bulan."
          baris={seriBulanan}
          kunciSeri1={kunciAngka(seriBulanan, ['penerimaan', 'pendapatan', 'masuk', 'kasMasuk', 'income', 'debit'])}
          kunciSeri2={kunciAngka(seriBulanan, ['pengeluaran', 'beban', 'keluar', 'kasKeluar', 'expense', 'credit'])}
          namaSeri1="Penerimaan"
          namaSeri2="Pengeluaran"
          memuat={memuat}
        />

        <GrafikGaris
          judul="Tren Saldo Kas & Bank"
          deskripsi="Pergerakan saldo kas dan bank dari waktu ke waktu."
          baris={seriSaldo}
          kunciSeri1={kunciPertama(seriSaldo, ['kas', 'saldoKas', 'kasTunai', 'cash'])}
          kunciSeri2={kunciPertama(seriSaldo, ['bank', 'saldoBank'])}
          memuat={memuat}
        />

        <GrafikBatang
          judul="Anggaran vs Realisasi per Unit"
          deskripsi="Serapan anggaran tiap unit pada periode terpilih."
          baris={seriAnggaran}
          kunciSeri1={kunciAngka(seriAnggaran, ['anggaran', 'totalFinal', 'totalAnggaran', 'finalAmount', 'budget', 'planned'])}
          kunciSeri2={kunciAngka(seriAnggaran, ['realisasi', 'totalRealized', 'realizedAmount', 'released', 'used'])}
          namaSeri1="Anggaran"
          namaSeri2="Realisasi"
          memuat={memuat}
        />

        <GrafikLingkaran
          judul="Pengeluaran per Kategori"
          deskripsi="Komposisi pengeluaran berdasarkan kategori beban."
          baris={seriKategori}
          kunciNilai={kunciAngka(seriKategori, ['total', 'amount', 'nilai', 'jumlah', 'value', 'totalAmount'])}
          memuat={memuat}
        />
      </div>
    </div>
  )
}

function PesanGrafikKosong({ pesan }: { pesan: string }) {
  return (
    <div className="flex h-[300px] items-center justify-center rounded-lg border border-dashed border-netral-300 bg-netral-50 px-6 text-center">
      <p className="max-w-sm text-sm text-netral-500">{pesan}</p>
    </div>
  )
}

function GrafikBatang({
  judul,
  deskripsi,
  baris,
  kunciSeri1,
  kunciSeri2,
  namaSeri1,
  namaSeri2,
  memuat,
}: {
  judul: string
  deskripsi: string
  baris: BarisGrafik[]
  kunciSeri1: string | null
  kunciSeri2: string | null
  namaSeri1: string
  namaSeri2: string
  memuat: boolean
}) {
  const label = kunciPertama(baris, KANDIDAT_LABEL)
  const siap = baris.length > 0 && label !== null && (kunciSeri1 !== null || kunciSeri2 !== null)

  return (
    <Card>
      <CardHeader judul={judul} deskripsi={deskripsi} />
      <div className="mt-3">
        {memuat && baris.length === 0 ? (
          <SkeletonGrafik />
        ) : siap ? (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={baris} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey={label ?? undefined} tick={{ fontSize: 12 }} />
              <YAxis tickFormatter={(v: unknown) => formatAngka(Number(v))} tick={{ fontSize: 12 }} width={90} />
              <Tooltip formatter={(v: unknown) => formatRupiah(Number(v))} />
              <Legend />
              {kunciSeri1 ? <Bar dataKey={kunciSeri1} name={namaSeri1} fill="#15803d" radius={[3, 3, 0, 0]} /> : null}
              {kunciSeri2 ? <Bar dataKey={kunciSeri2} name={namaSeri2} fill="#b45309" radius={[3, 3, 0, 0]} /> : null}
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <PesanGrafikKosong pesan="Data grafik belum tersedia dari endpoint dashboard untuk filter ini." />
        )}
      </div>
    </Card>
  )
}

function GrafikGaris({
  judul,
  deskripsi,
  baris,
  kunciSeri1,
  kunciSeri2,
  memuat,
}: {
  judul: string
  deskripsi: string
  baris: BarisGrafik[]
  kunciSeri1: string | null
  kunciSeri2: string | null
  memuat: boolean
}) {
  const label = kunciPertama(baris, KANDIDAT_LABEL)
  const siap = baris.length > 0 && label !== null && (kunciSeri1 !== null || kunciSeri2 !== null)

  return (
    <Card>
      <CardHeader judul={judul} deskripsi={deskripsi} />
      <div className="mt-3">
        {memuat && baris.length === 0 ? (
          <SkeletonGrafik />
        ) : siap ? (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={baris} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey={label ?? undefined} tick={{ fontSize: 12 }} />
              <YAxis tickFormatter={(v: unknown) => formatAngka(Number(v))} tick={{ fontSize: 12 }} width={90} />
              <Tooltip formatter={(v: unknown) => formatRupiah(Number(v))} />
              <Legend />
              {kunciSeri1 ? (
                <Line type="monotone" dataKey={kunciSeri1} name="Kas" stroke="#166534" strokeWidth={2} dot={false} />
              ) : null}
              {kunciSeri2 ? (
                <Line type="monotone" dataKey={kunciSeri2} name="Bank" stroke="#1d4ed8" strokeWidth={2} dot={false} />
              ) : null}
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <PesanGrafikKosong pesan="Seri saldo kas & bank belum tersedia dari endpoint dashboard untuk filter ini." />
        )}
      </div>
    </Card>
  )
}

function GrafikLingkaran({
  judul,
  deskripsi,
  baris,
  kunciNilai,
  memuat,
}: {
  judul: string
  deskripsi: string
  baris: BarisGrafik[]
  kunciNilai: string | null
  memuat: boolean
}) {
  const label = kunciPertama(baris, KANDIDAT_LABEL)
  const siap = baris.length > 0 && label !== null && kunciNilai !== null

  const data = useMemo(() => {
    if (!siap || !label || !kunciNilai) return [] as Array<{ name: string; value: number }>
    return baris
      .map((b) => ({ name: String(b[label] ?? '-'), value: Number(b[kunciNilai] ?? 0) }))
      .filter((d) => Number.isFinite(d.value) && d.value !== 0)
  }, [baris, label, kunciNilai, siap])

  return (
    <Card>
      <CardHeader judul={judul} deskripsi={deskripsi} />
      <div className="mt-3">
        {memuat && baris.length === 0 ? (
          <SkeletonGrafik />
        ) : data.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie data={data} dataKey="value" nameKey="name" outerRadius={110} innerRadius={45}>
                {data.map((_, i) => (
                  <Cell key={i} fill={WARNA_PIE[i % WARNA_PIE.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(v: unknown) => formatRupiah(Number(v))} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        ) : (
          <PesanGrafikKosong pesan="Rincian pengeluaran per kategori belum tersedia dari endpoint dashboard." />
        )}
      </div>
    </Card>
  )
}
